<template>
  <t-content main class="tournaments-history-view" data-qe-id="tournament-history-view">
    <c-loading-indicator class="modal-spinner" :show="isLoading" />

    <c-tournament-header :zone="zoneId" :language="$i18n.locale" />
    <c-navigation>
      <router-link :to="{ path: `/tournaments/${zoneId}`, query: { table: 'history' } }" data-qe-id="back-button">{{
        $t('tournamentHistory.main.back_button')
      }}</router-link>
    </c-navigation>

    <div class="tournaments-view" v-if="!isLoading">
      <t-text class="tournament-header">
        <h1 class="tournaments-headline" data-qe-id="tournament-name">{{ tournament.name }}</h1>
        <span
          class="winning-distribution-info"
          @click="events.$emit('tournament-info-modal', { tournamentUi: tournament, trackId: 'header' })"
          data-qe-id="win-distribution-trigger"
        >
          <i class="icf-info icon-info"></i>
        </span>
      </t-text>
      <c-joined-template class="joined-template-board" :isMasters="isMasters">
        <c-games-overview @onGame="onGame" :games="games" :is-history-mode="true">
          <template v-slot:header>
            <c-finished-laurel
              v-if="tournament.tournamentRank"
              class="tournament-finished-laurel"
              :state="TOURNAMENT_LAUREL_STATE.HISTORY"
              :score="tournament.tournamentScore"
              :rank="tournament.tournamentRank"
            ></c-finished-laurel>
          </template>
        </c-games-overview>
        <!-- modal content -->
        <m-result-modal
          v-if="showResultModal"
          @close="showResultModal = false"
          :competition-id="gameSelectedCompetitionId"
        />
      </c-joined-template>
      <section class="tournament-info">
        <!-- TODO vue3: this could never be worked - as tournament-ui attribute is missing which
             throws an error inside the component -->
        <c-tournament-info
          :tournament-ui="tournament"
          :meta-data="tournament.metaData"
          :dates="tournament.dates"
          :show-registration-end="false"
        />
      </section>
      <t-container>
        <div class="t-table-header">
          {{ $t('tournamentHistory.main.table_title', { tournamentName: tournament.name }) }}
        </div>
        <m-tournament-table :t-id="tId" :show-history-mode="true" data-qe-id="table-history" />
      </t-container>
    </div>
  </t-content>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/store'
import CGamesOverview from '@/components/CGamesOverview.vue'
import CTournamentInfo from '@/components/CTournamentInfo.vue'
import MTournamentTable from '@/modules/MTournamentTable.vue'
import CTournamentHeader from '@/components/CTournamentHeader.vue'
import CJoinedTemplate from '@/components/CJoinedTemplate.vue'
import CNavigation from '@/components/CNavigation.vue'
import { ERROR_CODES } from '@/model/ErrorCodes'
import MResultModal from '@/modules/MResultModal.vue'
import CFinishedLaurel from '@/components/CTournamentLaurel.vue'
import { TOURNAMENT_LAUREL_STATE } from '@/components/CTournamentLaurel.vue'
import { events } from '@gd/event-bus'

const props = defineProps<{ tId: string; zoneId: string }>()
const emit = defineEmits(['tournament-info-modal'])
const router = useRouter()
const store = useStore()

const isLoading = ref(true)
const gameSelectedCompetitionId = ref('')
const showResultModal = ref(false)

const tournament = computed(() => {
  return store.getters['history/tournamentUi']
})

const isMasters = computed(() => {
  return store.getters['history/tournamentUi'].isMasters
})

const games = computed(() => {
  const games = store.getters['history/tournamentUi'].games
  // console.log('games', games)
  return games
})

function onGame(game: any) {
  gameSelectedCompetitionId.value = game.competitionId
  showResultModal.value = true
}

store.commit('history/zoneId', props.zoneId)
store
  .dispatch('history/fetchTournamentHistoryById', props.tId)
  .then(() => {
    isLoading.value = false
  })
  .catch((err: any) => {
    if (err && err.errorCode === 401) {
      router.replace('/extern/login')
    } else {
      router.push({ path: `/error/ ${(err && err.errorCode) || ERROR_CODES.UNKNOWN}/${props.zoneId}` })
    }
  })
</script>

<style lang="less" scoped>
@import '~@npm/@gd/common-styles/lib/color-vars';
@import '../styles/vars';
.tournaments-history-view {
  text-align: center;
  padding-bottom: 5.625em;
  .joined-template-board {
    margin-top: 3em;
  }
  .tournament-header {
    display: flex;
    justify-content: center;
    align-items: center;
    justify-items: center;
  }
  .tournaments-headline {
    display: inline-block;
    text-transform: uppercase;
  }
  .winning-distribution-info {
    cursor: pointer;
    .icon-info {
      color: @lobbyStatus--open;
      position: relative;
      font-size: 1.25em;
      top: 0.65em;
      margin-left: 0.55em;
      transition: color 150ms;
      &:hover {
        color: lighten(@lobbyStatus--open, 10%);
      }
    }
  }
  .tournament-info {
    max-width: 80%;
    margin: 3em auto;
  }
}
</style>
